import { BaseRowData, BaseRowMetadata, Operation } from 'apiConnector/secondary-market/batch/base';
import {
  OrderType,
  ReloadResponse,
  ResetRequest,
  Row,
  SearchResponse,
} from 'apiConnector/secondary-market/batch/model';
import type { EditableFieldInfo, IBlotterPreferenceState } from 'components/shared/Blotter/Grid/blotter.model';
import { RowData, SearchType } from 'state/workspaces/secondary-market/batch/model';

export type SecondaryMarketBatchActions = ReturnType<
  (typeof secondaryMarketBatchActions)[keyof typeof secondaryMarketBatchActions]
>;

export type SecondaryMarketBatchReloadOrdersAction = ReturnType<(typeof secondaryMarketBatchActions)['reloadOrders']>;

type SecondaryMarketBatchReloadSucceeded = {
  type: 'SECONDARY_MARKET_BATCH_RELOAD_SUCCEEDED';
  payload: { orderType: OrderType; response: ReloadResponse };
};
export type OperationResponseRowByOrderType = Row<BaseRowData, BaseRowMetadata & { endZonedDateTimeToTrade?: string }>;

type SecondaryMarketBatchOperationSucceeded = {
  type: 'SECONDARY_MARKET_BATCH_OPERATION_SUCCEEDED';
  payload: { orderType: OrderType; operation: Operation; rows: OperationResponseRowByOrderType[] };
};

export type SelectedRow = { id: string; selected: boolean };
export const secondaryMarketBatchActions = {
  workspaceRequested() {
    return {
      type: 'SECONDARY_MARKET_BATCH_WORKSPACE_REQUESTED',
    } as const;
  },
  workspaceReceived() {
    return {
      type: 'SECONDARY_MARKET_BATCH_WORKSPACE_RECEIVED',
    } as const;
  },
  workspaceFailed(error: unknown) {
    return {
      type: 'SECONDARY_MARKET_BATCH_WORKSPACE_FAILED',
      payload: { error },
    } as const;
  },
  searchTypeChanged(orderType: OrderType, searchType: SearchType) {
    return {
      type: 'SECONDARY_MARKET_BATCH_SEARCH_TYPE_CHANGED',
      payload: { orderType, searchType },
    } as const;
  },
  orderTypeChange: (orderType: OrderType) =>
    ({
      type: 'SECONDARY_MARKET_BATCH_ORDER_TYPE_CHANGED',
      payload: { orderType },
    }) as const,
  resetRequested(orderType: OrderType, request: ResetRequest) {
    return {
      type: 'SECONDARY_MARKET_BATCH_RESET_REQUESTED',
      payload: { orderType, request },
    } as const;
  },
  resetSucceeded(orderType: OrderType, response: SearchResponse) {
    return {
      type: 'SECONDARY_MARKET_BATCH_RESET_SUCCEEDED',
      payload: { orderType, response },
    } as const;
  },
  resetFailed(orderType: OrderType, rowsIds: string[], error: unknown) {
    return {
      type: 'SECONDARY_MARKET_BATCH_RESET_FAILED',
      payload: { orderType, rowsIds, error },
    } as const;
  },
  modifyRequested(orderType: OrderType, rows: RowData[]) {
    return {
      type: 'SECONDARY_MARKET_BATCH_MODIFY_REQUESTED',
      payload: { orderType, rows },
    } as const;
  },
  modifySucceeded(orderType: OrderType, response: SearchResponse) {
    return {
      type: 'SECONDARY_MARKET_BATCH_MODIFY_SUCCEEDED',
      payload: { orderType, response },
    } as const;
  },
  modifyFailed(orderType: OrderType, rows: RowData[], error: unknown) {
    return {
      type: 'SECONDARY_MARKET_BATCH_MODIFY_FAILED',
      payload: { orderType, rows, error },
    } as const;
  },
  reloadRequested(orderType: OrderType) {
    return {
      type: 'SECONDARY_MARKET_BATCH_RELOAD_REQUESTED',
      payload: { orderType },
    } as const;
  },
  reloadSucceeded(orderType: OrderType, response: ReloadResponse): SecondaryMarketBatchReloadSucceeded {
    return {
      type: 'SECONDARY_MARKET_BATCH_RELOAD_SUCCEEDED',
      payload: { orderType, response },
    } as SecondaryMarketBatchReloadSucceeded;
  },
  reloadFailed(orderType: OrderType, error: unknown) {
    return {
      type: 'SECONDARY_MARKET_BATCH_RELOAD_FAILED',
      payload: { orderType, error },
    } as const;
  },
  searchSucceeded(orderType: OrderType, response: SearchResponse) {
    return {
      type: 'SECONDARY_MARKET_BATCH_SEARCH_SUCCEEDED',
      payload: { orderType, response },
    } as const;
  },
  searchFailed(orderType: OrderType, error: unknown) {
    return {
      type: 'SECONDARY_MARKET_BATCH_SEARCH_FAILED',
      payload: { orderType, error },
    } as const;
  },
  updatePreference(orderType: OrderType, preference: IBlotterPreferenceState) {
    return {
      type: 'SECONDARY_MARKET_BATCH_UPDATE_PREFERENCE',
      payload: { orderType, preference },
    } as const;
  },
  selectedRowsChanged(orderType: OrderType, rows: SelectedRow[]) {
    return {
      type: 'SECONDARY_MARKET_BATCH_SELECTED_ROWS_CHANGED',
      payload: { orderType, rows },
    } as const;
  },
  deleteRowsRequested(orderType: OrderType, rowIds: string[]) {
    return {
      type: 'SECONDARY_MARKET_BATCH_DELETE_ROWS_REQUESTED',
      payload: { orderType, rowIds },
    } as const;
  },
  deleteRowsSucceeded(orderType: OrderType, rowIds: string[]) {
    return {
      type: 'SECONDARY_MARKET_BATCH_DELETE_ROWS_SUCCEEDED',
      payload: { orderType, rowIds },
    } as const;
  },
  deleteRowsFailed(orderType: OrderType, rowIds: string[], error: unknown) {
    return {
      type: 'SECONDARY_MARKET_BATCH_DELETE_ROWS_FAILED',
      payload: { orderType, rowIds, error },
    } as const;
  },
  operationRequested(orderType: OrderType, operation: Operation, rowIds: string[]) {
    return {
      type: 'SECONDARY_MARKET_BATCH_OPERATION_REQUESTED',
      payload: { orderType, operation, rowIds },
    } as const;
  },
  operationSucceeded(orderType: OrderType, operation: Operation, rows: OperationResponseRowByOrderType[]) {
    return {
      type: 'SECONDARY_MARKET_BATCH_OPERATION_SUCCEEDED',
      payload: { orderType, operation, rows },
    } as SecondaryMarketBatchOperationSucceeded;
  },
  operationFailed(orderType: OrderType, operation: Operation, rowIds: string[], error: unknown) {
    return {
      type: 'SECONDARY_MARKET_BATCH_OPERATION_FAILED',
      payload: { orderType, operation, rowIds, error },
    } as const;
  },
  rowQuoteExpired(orderType: OrderType, rowId: string) {
    return {
      type: 'SECONDARY_MARKET_BATCH_ROW_QUOTE_EXPIRED',
      payload: { orderType, rowId },
    } as const;
  },
  setCellEditableInfo(orderType: OrderType, rowId: string, field: keyof RowData, info: EditableFieldInfo) {
    return {
      type: 'SECONDARY_MARKET_BATCH_SET_CELL_EDITABLE_INFO',
      payload: { orderType, rowId, field, info },
    } as const;
  },
  reloadOrders(orderType: OrderType, rows: RowData[]) {
    return {
      type: 'SECONDARY_MARKET_BATCH_RELOAD_ORDERS',
      payload: { orderType, rows },
    } as const;
  },
  clearQuoteData(rowId: string) {
    return {
      type: 'SECONDARY_MARKET_BATCH_CLEAR_QUOTE_DATA',
      payload: { rowId },
    } as const;
  },
};
